import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Server, Plus, Trash2, Save, X, Box, Battery, Edit3, Network } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import PortConfigurator from './PortConfigurator';
import CableManager from './CableManager';


const RACK_U_HEIGHT = 42;

const DEVICE_TYPES = [
    { type: 'Server', u: 1, icon: Server, color: 'bg-blue-500/15 border-blue-500/20 text-blue-400' },
    { type: 'Switch', u: 1, icon: Box, color: 'bg-emerald-500/15 border-emerald-500/20 text-emerald-400' },
    { type: 'Firewall', u: 1, icon: Box, color: 'bg-red-500/15 border-red-500/20 text-red-400' },
    { type: 'NAS', u: 2, icon: Server, color: 'bg-orange-500/15 border-orange-500/20 text-orange-400' },
    { type: 'UPS', u: 2, icon: Box, color: 'bg-stone-500/15 border-stone-500/20 text-stone-400' },
    { type: 'Battery Backup', u: 2, icon: Battery, color: 'bg-stone-500/15 border-stone-500/20 text-stone-400' },
    { type: 'AP', u: 1, icon: Box, color: 'bg-purple-500/15 border-purple-500/20 text-purple-400' },
    { type: 'Modem', u: 1, icon: Box, color: 'bg-cyan-500/15 border-cyan-500/20 text-cyan-400' },
    { type: 'ISP', u: 1, icon: Box, color: 'bg-pink-500/15 border-pink-500/20 text-pink-400' },
    { type: 'Gateway', u: 1, icon: Box, color: 'bg-indigo-500/15 border-indigo-500/20 text-indigo-400' },
    { type: 'Router', u: 1, icon: Box, color: 'bg-violet-500/15 border-violet-500/20 text-violet-400' },
    { type: 'Patch Panel', u: 1, icon: Box, color: 'bg-yellow-500/15 border-yellow-500/20 text-yellow-500' }
];

// Solid colors for rack slots (more visible)
const DEVICE_SLOT_COLORS = {
    Server: 'bg-blue-600',
    Switch: 'bg-emerald-600',
    Firewall: 'bg-red-600',
    NAS: 'bg-orange-600',
    UPS: 'bg-stone-600',
    'Battery Backup': 'bg-stone-500',
    AP: 'bg-purple-600',
    Modem: 'bg-cyan-600',
    ISP: 'bg-pink-600',
    Gateway: 'bg-indigo-600',
    Router: 'bg-violet-600',
    'Patch Panel': 'bg-yellow-600'
};

const RackBuilder = ({ rack, onBack, authenticatedFetch = fetch }) => {
    const [devices, setDevices] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('devices'); // 'devices' | 'cables'

    // Form State (shared for add + edit)
    const [newDeviceType, setNewDeviceType] = useState(DEVICE_TYPES[0].type);
    const [newDeviceHeight, setNewDeviceHeight] = useState(1);
    const [newDeviceModel, setNewDeviceModel] = useState('');
    const [newDeviceStatus, setNewDeviceStatus] = useState('Active');
    const [newDeviceNotes, setNewDeviceNotes] = useState('');
    const [newDeviceUpgrade, setNewDeviceUpgrade] = useState(null); // null | { model, description, price }

    // Default smart port layouts based on type
    const getDefaultPortBanks = (type) => {
        switch (type) {
            case 'Switch': return [
                { id: Date.now(), count: 24, type: 'LAN', columns: 12, xOffset: -0.4, yOffset: 0 },
                { id: Date.now() + 1, count: 4, type: 'SFP', columns: 4, xOffset: 0.6, yOffset: 0 }
            ];
            case 'Patch Panel': return [
                { id: Date.now(), count: 48, type: 'Normal', columns: 24, xOffset: -0.4, yOffset: 0 }
            ];
            case 'Gateway':
            case 'Router':
            case 'Firewall': return [
                { id: Date.now(), count: 2, type: 'WAN', columns: 2, xOffset: -0.4, yOffset: 0 },
                { id: Date.now() + 1, count: 8, type: 'LAN', columns: 4, xOffset: 0, yOffset: 0 }
            ];
            default: return []; // Things like UPS or AP don't get front-facing visual ports by default
        }
    };

    const [portBanks, setPortBanks] = useState(getDefaultPortBanks(DEVICE_TYPES[0].type));

    // Port Bank Handlers
    const handleAddPortBank = () => {
        setPortBanks([...portBanks, { id: Date.now(), count: 8, type: 'LAN', columns: 8, xOffset: 0, yOffset: 0 }]);
    };

    const removePortBank = (id) => {
        setPortBanks(prev => prev.filter(b => b.id !== id));
    };

    const fetchDevices = useCallback(async () => {
        try {
            const res = await authenticatedFetch(`/api/network/devices?rackId=${rack.id}`);
            const data = await res.json();
            setDevices(data);
            setLoading(false);
        } catch (err) {
            console.error(err);
        }
    }, [authenticatedFetch, rack.id]);

    useEffect(() => {
        fetchDevices();
    }, [fetchDevices]);

    const getDeviceAtSlot = (u) => {
        return devices.find(d => {
            const start = d.position_u;
            const end = d.position_u + (d.height_u || 1) - 1;
            return u >= start && u <= end;
        });
    };

    const handleSlotClick = (u) => {
        const device = getDeviceAtSlot(u);
        if (device) {
            setSelectedDevice(device);
            setSelectedSlot(null);
        } else {
            setSelectedSlot(u);
            setSelectedDevice(null);
            setNewDeviceModel('');
            setNewDeviceHeight(1);
            setNewDeviceType('Server');
            setNewDeviceStatus('Active');
            setPortBanks(getDefaultPortBanks('Server'));
        }
    };

    const handleTypeSelect = (dt) => {
        setNewDeviceType(dt.type);
        setNewDeviceHeight(dt.u);
        setPortBanks(getDefaultPortBanks(dt.type)); // Smart pre-populate

        if (dt.type === 'ISP' || dt.type === 'Modem') {
            let topSlot = RACK_U_HEIGHT;
            while (topSlot >= 1 && getDeviceAtSlot(topSlot)) {
                topSlot--;
            }
            if (topSlot >= 1) setSelectedSlot(topSlot);
        }
    };

    const handleAddDevice = async (e) => {
        e.preventDefault();
        console.log("Submit triggered. Validating placement...");
        if (selectedSlot + newDeviceHeight - 1 > RACK_U_HEIGHT) {
            alert("The device is too tall for this position!");
            return;
        }
        for (let i = 0; i < newDeviceHeight; i++) {
            if (getDeviceAtSlot(selectedSlot + i)) {
                alert("Cannot place device here, it overlaps with another device.");
                return;
            }
        }

        try {
            const payload = {
                rackId: rack.id,
                positionU: selectedSlot,
                heightU: newDeviceHeight,
                type: newDeviceType,
                modelName: newDeviceModel,
                status: newDeviceStatus,
                specs: { portBanks, notes: newDeviceNotes, upgrade: newDeviceUpgrade }
            };

            const response = await authenticatedFetch('/api/network/devices', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json();
                console.error("Server error:", errData);
            }

            await fetchDevices();
            setSelectedSlot(null);
        } catch (err) {
            console.error("Error saving device:", err);
        }
    };

    // Enter edit mode — populate form fields from the selected device
    const handleEditDevice = () => {
        if (!selectedDevice) return;
        setNewDeviceType(selectedDevice.type);
        setNewDeviceModel(selectedDevice.model_name || '');
        setNewDeviceStatus(selectedDevice.status || 'Active');
        setNewDeviceHeight(selectedDevice.height_u || 1);
        // Parse specs.portBanks if it exists
        const specs = typeof selectedDevice.specs === 'string'
            ? JSON.parse(selectedDevice.specs || '{}')
            : (selectedDevice.specs || {});
        setPortBanks(specs.portBanks || getDefaultPortBanks(selectedDevice.type));
        setNewDeviceNotes(specs.notes || '');
        setNewDeviceUpgrade(specs.upgrade || null);
        setIsEditing(true);
    };

    // Save edits via PUT
    const handleUpdateDevice = async (e) => {
        e.preventDefault();
        if (!selectedDevice) return;

        try {
            const payload = {
                rackId: rack.id,
                positionU: selectedDevice.position_u,
                heightU: newDeviceHeight,
                type: newDeviceType,
                modelName: newDeviceModel,
                status: newDeviceStatus,
                specs: { portBanks, notes: newDeviceNotes, upgrade: newDeviceUpgrade }
            };

            const response = await authenticatedFetch(`/api/network/devices/${selectedDevice.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errData = await response.json();
                console.error('Update error:', errData);
                return;
            }

            await fetchDevices();
            setSelectedDevice(null);
            setIsEditing(false);
        } catch (err) {
            console.error('Error updating device:', err);
        }
    };

    const handleDeleteDevice = async () => {
        if (!selectedDevice) return;
        if (!window.confirm('Are you sure you want to remove this device?')) return;

        try {
            await authenticatedFetch(`/api/network/devices/${selectedDevice.id}`, { method: 'DELETE' });
            await fetchDevices();
            setSelectedDevice(null);
            setIsEditing(false);
        } catch (err) {
            console.error(err);
        }
    };

    const renderRackUnits = () => {
        const slots = [];
        for (let u = RACK_U_HEIGHT; u >= 1; u--) {
            const occupyingDevice = getDeviceAtSlot(u);

            if (occupyingDevice) {
                const topU = occupyingDevice.position_u + (occupyingDevice.height_u || 1) - 1;
                if (u === topU) {
                    const heightStyle = { height: `${(occupyingDevice.height_u || 1) * 3}rem` };
                    const slotColor = DEVICE_SLOT_COLORS[occupyingDevice.type] || 'bg-white/10';
                    slots.push(
                        <motion.div
                            layoutId={occupyingDevice.id}
                            key={u}
                            onClick={() => handleSlotClick(u)}
                            className={`relative w-full rounded-lg flex items-center px-5 cursor-pointer group ${slotColor} hover:brightness-110 transition-all border border-white/[0.06]`}
                            style={heightStyle}
                        >
                            <div className="flex-1">
                                <div className="text-white font-semibold text-sm">{occupyingDevice.model_name || occupyingDevice.type}</div>
                                <div className="text-white/40 text-[10px] uppercase tracking-wider">{occupyingDevice.type} • {occupyingDevice.status}</div>
                            </div>
                            <div className="text-white/20 font-mono text-[10px] absolute right-3 top-2 tracking-wider">U{occupyingDevice.position_u}</div>
                            <div className={`w-1.5 h-1.5 rounded-full absolute right-3 bottom-2.5 ${occupyingDevice.status === 'Critical' ? 'bg-red-400 animate-pulse' : occupyingDevice.status === 'Warning' ? 'bg-yellow-400' : occupyingDevice.status === 'Legacy' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                        </motion.div>
                    );
                } else {
                    continue;
                }
            } else {
                const isSelected = selectedSlot !== null && u >= selectedSlot && u < selectedSlot + newDeviceHeight;
                slots.push(
                    <div
                        key={u}
                        onClick={() => handleSlotClick(u)}
                        className={`h-12 w-full border-b border-white/[0.03] flex items-center px-5 cursor-pointer hover:bg-white/[0.03] transition-colors group relative ${isSelected ? 'bg-blue-500/10 ring-1 ring-blue-500/30 z-10' : ''}`}
                    >
                        <span className="text-white/10 font-mono text-[10px] w-6 group-hover:text-white/25 tracking-wider">{u}</span>
                        <div className="flex-1 flex justify-center opacity-0 group-hover:opacity-100">
                            <Plus size={12} className="text-white/15" />
                        </div>
                    </div>
                );
            }
        }
        return slots;
    };

    const inputClass = "w-full bg-[#0a0a0a] border border-white/10 text-white/80 text-sm px-3 py-2.5 rounded-lg focus:border-white/20 focus:outline-none transition-colors";
    const labelClass = "block text-[10px] font-bold text-white/20 mb-1.5 uppercase tracking-widest";

    return (
        <div className="flex flex-col h-full text-white" style={{ background: 'linear-gradient(180deg, #080808 0%, #0a0a0a 40%, #0d0d0d 100%)' }}>
            {/* Header */}
            <div className="flex items-center gap-4 px-8 py-5 border-b border-white/[0.04] bg-[#0a0a0a]/80 backdrop-blur-2xl">
                <button onClick={onBack} className="p-2 hover:bg-white/[0.04] rounded-xl text-white/20 hover:text-white/60 transition-all">
                    <ArrowLeft size={18} />
                </button>
                <div className="flex-1">
                    <h1 className="text-lg font-bold text-white tracking-tight flex items-center gap-2.5">
                        <Server size={18} className="text-white/20" />
                        {rack.name}
                    </h1>
                    <p className="text-[10px] text-white/15 uppercase tracking-widest mt-0.5">Rack Configuration</p>
                </div>
                <div className="flex items-center gap-6">
                    <div className="flex bg-[#0a0a0a] border border-white/10 p-1 rounded-lg">
                        <button
                            onClick={() => setActiveTab('devices')}
                            className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'devices' ? 'bg-white/10 text-white shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                        >
                            <Server size={14} /> Devices
                        </button>
                        <button
                            onClick={() => setActiveTab('cables')}
                            className={`px-4 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all ${activeTab === 'cables' ? 'bg-blue-500/20 text-blue-400 shadow-sm' : 'text-white/40 hover:text-white/70'}`}
                        >
                            <Network size={14} /> Cables
                        </button>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-white/20 uppercase tracking-widest">
                        <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> {devices.length} Devices
                    </div>
                </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
                {/* Rack Visualizer */}
                <div className="flex-1 overflow-y-auto p-4 md:p-8 flex justify-center min-h-[50vh] md:min-h-0">
                    <div className="w-full max-w-2xl bg-[#0a0a0a]/50 border border-white/[0.04] rounded-2xl relative h-fit pb-8">
                        {/* Rack Rails */}
                        <div className="absolute left-0 top-0 bottom-0 w-4 md:w-8 border-r border-white/[0.03] bg-white/[0.01]" />
                        <div className="absolute right-0 top-0 bottom-0 w-4 md:w-8 border-l border-white/[0.03] bg-white/[0.01]" />

                        <div className="flex flex-col w-full h-full px-4 md:px-8 py-4 space-y-[1px]">
                            {loading ? (
                                <div className="text-center py-20 text-white/15 font-mono text-sm tracking-widest animate-pulse">Loading Rack...</div>
                            ) : renderRackUnits()}
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="w-full md:w-96 border-t md:border-t-0 md:border-l border-white/[0.04] bg-[#0a0a0a]/80 backdrop-blur-2xl p-4 md:p-6 overflow-y-auto md:h-full shrink-0">
                    {activeTab === 'cables' ? (
                        <CableManager rackId={rack.id} devices={devices} authenticatedFetch={authenticatedFetch} />
                    ) : (
                        <AnimatePresence mode="wait">
                            {selectedSlot ? (
                            <motion.div
                                key="add-form"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                            >
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h2 className="text-base font-bold text-white">Add Device</h2>
                                        <p className="text-[10px] text-white/20 uppercase tracking-widest mt-1">Position U{selectedSlot}</p>
                                    </div>
                                    <button onClick={() => setSelectedSlot(null)} className="text-white/15 hover:text-white/40 transition-colors">
                                        <X size={16} />
                                    </button>
                                </div>

                                <form onSubmit={handleAddDevice} className="space-y-6">
                                    {/* Type Selection */}
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Device Type</label>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            {DEVICE_TYPES.map(dt => (
                                                <button
                                                    key={dt.type}
                                                    type="button"
                                                    onClick={() => handleTypeSelect(dt)}
                                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] border transition-all ${newDeviceType === dt.type
                                                        ? dt.color + ' font-semibold'
                                                        : 'bg-white/[0.02] border-white/[0.04] text-white/30 hover:bg-white/[0.04] hover:text-white/50'
                                                        }`}
                                                >
                                                    <dt.icon size={12} />
                                                    {dt.type}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Basic Info */}
                                    <div className="space-y-3">
                                        <div>
                                            <label className={labelClass}>Model Name</label>
                                            <input
                                                type="text"
                                                value={newDeviceModel}
                                                onChange={e => setNewDeviceModel(e.target.value)}
                                                className={inputClass}
                                                placeholder="e.g. Ubiquiti Dream Machine"
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Device State</label>
                                            <select
                                                value={newDeviceStatus}
                                                onChange={e => setNewDeviceStatus(e.target.value)}
                                                className={inputClass}
                                                style={{ colorScheme: 'dark' }}
                                            >
                                                <option value="Active" style={{ background: '#1a1a1a' }}>Active</option>
                                                <option value="Warning" style={{ background: '#1a1a1a' }}>Warning</option>
                                                <option value="Critical" style={{ background: '#1a1a1a' }}>Critical</option>
                                                <option value="Legacy" style={{ background: '#1a1a1a' }}>Legacy</option>
                                            </select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className={labelClass}>Row Start</label>
                                                <input disabled value={selectedSlot} className="w-full bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2.5 text-white/20 cursor-not-allowed text-sm" />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Height (U)</label>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={10}
                                                    value={newDeviceHeight}
                                                    onChange={e => setNewDeviceHeight(parseInt(e.target.value))}
                                                    className={inputClass}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Port Configuration */}
                                    <div className="pt-3 border-t border-white/[0.04]">
                                        <label className={labelClass}>Port Configuration</label>
                                        <PortConfigurator
                                            portBanks={portBanks}
                                            onChange={setPortBanks}
                                            onAddBank={handleAddPortBank}
                                            onRemoveBank={removePortBank}
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            className="w-full py-3 bg-white/[0.06] hover:bg-white/[0.10] text-white/60 hover:text-white rounded-xl font-semibold text-xs uppercase tracking-widest transition-all border border-white/[0.06] hover:border-white/[0.12] flex items-center justify-center gap-2 shadow-lg"
                                        >
                                            <Save size={14} />
                                            Save Device
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        ) : selectedDevice ? (
                            <motion.div
                                key={isEditing ? 'edit-form' : 'view-details'}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                            >
                                <div className="flex justify-between items-center mb-6">
                                    <div>
                                        <h2 className="text-base font-bold text-white">{isEditing ? 'Edit Device' : 'Device Details'}</h2>
                                        <p className="text-[10px] text-white/20 uppercase tracking-widest mt-1">{selectedDevice.type} — U{selectedDevice.position_u}</p>
                                    </div>
                                    <button onClick={() => { setSelectedDevice(null); setIsEditing(false); }} className="text-white/15 hover:text-white/40 transition-colors">
                                        <X size={16} />
                                    </button>
                                </div>

                                {isEditing ? (
                                    /* ——— Edit Mode Form ——— */
                                    <form onSubmit={handleUpdateDevice} className="space-y-6">
                                        {/* Type Selection */}
                                        <div className="space-y-1.5">
                                            <label className={labelClass}>Device Type</label>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {DEVICE_TYPES.map(dt => (
                                                    <button
                                                        key={dt.type}
                                                        type="button"
                                                        onClick={() => { setNewDeviceType(dt.type); setNewDeviceHeight(dt.u); }}
                                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] border transition-all ${newDeviceType === dt.type
                                                            ? dt.color + ' font-semibold'
                                                            : 'bg-white/[0.02] border-white/[0.04] text-white/30 hover:bg-white/[0.04] hover:text-white/50'
                                                            }`}
                                                    >
                                                        <dt.icon size={12} />
                                                        {dt.type}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Basic Info */}
                                        <div className="space-y-3">
                                            <div>
                                                <label className={labelClass}>Model Name</label>
                                                <input
                                                    type="text"
                                                    value={newDeviceModel}
                                                    onChange={e => setNewDeviceModel(e.target.value)}
                                                    className={inputClass}
                                                    placeholder="e.g. Ubiquiti Dream Machine"
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Device State</label>
                                                <select
                                                    value={newDeviceStatus}
                                                    onChange={e => setNewDeviceStatus(e.target.value)}
                                                    className={inputClass}
                                                    style={{ colorScheme: 'dark' }}
                                                >
                                                    <option value="Active" style={{ background: '#1a1a1a' }}>Active</option>
                                                    <option value="Warning" style={{ background: '#1a1a1a' }}>Warning</option>
                                                    <option value="Critical" style={{ background: '#1a1a1a' }}>Critical</option>
                                                    <option value="Legacy" style={{ background: '#1a1a1a' }}>Legacy</option>
                                                </select>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className={labelClass}>Position</label>
                                                    <input disabled value={`U${selectedDevice.position_u}`} className="w-full bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2.5 text-white/20 cursor-not-allowed text-sm" />
                                                </div>
                                                <div>
                                                    <label className={labelClass}>Height (U)</label>
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={10}
                                                        value={newDeviceHeight}
                                                        onChange={e => setNewDeviceHeight(parseInt(e.target.value))}
                                                        className={inputClass}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Port Configuration */}
                                        <div className="pt-3 border-t border-white/[0.04]">
                                            <label className={labelClass}>Port Configuration</label>
                                            <PortConfigurator
                                                portBanks={portBanks}
                                                onChange={setPortBanks}
                                                onAddBank={handleAddPortBank}
                                                onRemoveBank={removePortBank}
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div className="pt-3 border-t border-white/[0.04]">
                                            <label className={labelClass}>Notes</label>
                                            <textarea
                                                className={`${inputClass} h-20 resize-none`}
                                                value={newDeviceNotes}
                                                onChange={e => setNewDeviceNotes(e.target.value)}
                                                placeholder="Serial numbers, tech notes, location details..."
                                            />
                                        </div>

                                        {/* Recommended Upgrade */}
                                        <div className="pt-3 border-t border-white/[0.04]">
                                            <label className={`${labelClass} text-blue-400/60`}>Recommended Upgrade</label>
                                            {newDeviceUpgrade ? (
                                                <div className="space-y-2">
                                                    <input className={inputClass} placeholder="Model (e.g. UniFi Pro Max 48)" value={newDeviceUpgrade.model} onChange={e => setNewDeviceUpgrade({ ...newDeviceUpgrade, model: e.target.value })} />
                                                    <input className={inputClass} placeholder="Description (e.g. 10Gbps uplink)" value={newDeviceUpgrade.description} onChange={e => setNewDeviceUpgrade({ ...newDeviceUpgrade, description: e.target.value })} />
                                                    <div className="flex gap-2">
                                                        <input className={`${inputClass} flex-1 font-mono text-emerald-400`} placeholder="Est. price" value={newDeviceUpgrade.price} onChange={e => setNewDeviceUpgrade({ ...newDeviceUpgrade, price: e.target.value })} />
                                                        <button type="button" onClick={() => setNewDeviceUpgrade(null)} className="px-3 py-2 bg-red-500/5 hover:bg-red-500/10 text-red-400/50 hover:text-red-400 text-[10px] uppercase tracking-widest rounded-lg border border-red-500/10 transition-all">Remove</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button type="button" onClick={() => setNewDeviceUpgrade({ model: '', description: '', price: '' })} className="w-full py-2.5 bg-white/[0.02] hover:bg-white/[0.04] text-white/20 hover:text-white/40 text-[10px] uppercase tracking-widest rounded-xl border border-dashed border-white/[0.06] hover:border-white/10 transition-all">
                                                    + Add Upgrade Recommendation
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex gap-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={() => setIsEditing(false)}
                                                className="flex-1 py-3 bg-white/[0.03] hover:bg-white/[0.06] text-white/40 hover:text-white/60 rounded-xl font-semibold text-xs uppercase tracking-widest transition-all border border-white/[0.04] flex items-center justify-center gap-2"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="flex-1 py-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400/70 hover:text-emerald-400 rounded-xl font-semibold text-xs uppercase tracking-widest transition-all border border-emerald-500/15 hover:border-emerald-500/30 flex items-center justify-center gap-2 shadow-lg"
                                            >
                                                <Save size={14} />
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>
                                ) : (
                                    /* ——— Read-Only Details ——— */
                                    <>
                                        <div className="bg-white/[0.02] rounded-xl border border-white/[0.04] p-5 mb-5">
                                            <h3 className="text-white font-semibold mb-1">{selectedDevice.model_name || 'Generic Device'}</h3>
                                            <p className="text-white/20 text-xs mb-4 uppercase tracking-wider">{selectedDevice.type}</p>

                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                <div className="bg-white/[0.02] p-3 rounded-lg border border-white/[0.03]">
                                                    <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Position</span>
                                                    <span className="text-white/60 font-mono text-sm">U{selectedDevice.position_u}</span>
                                                </div>
                                                <div className="bg-white/[0.02] p-3 rounded-lg border border-white/[0.03]">
                                                    <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Height</span>
                                                    <span className="text-white/60 font-mono text-sm">{selectedDevice.height_u}U</span>
                                                </div>
                                                <div className="bg-white/[0.02] p-3 rounded-lg border border-white/[0.03]">
                                                    <span className="text-[10px] text-white/15 block uppercase tracking-widest mb-0.5">Status</span>
                                                    <span className={`text-sm font-semibold ${selectedDevice.status === 'Active' ? 'text-emerald-400' : selectedDevice.status === 'Warning' ? 'text-yellow-400' : selectedDevice.status === 'Critical' ? 'text-red-400' : 'text-amber-400'}`}>{selectedDevice.status}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Notes */}
                                        {selectedDevice.specs?.notes && (
                                            <div className="bg-white/[0.02] rounded-xl border border-white/[0.04] p-4">
                                                <h3 className="text-[10px] font-bold text-white/20 uppercase tracking-widest mb-2">Notes</h3>
                                                <p className="text-white/40 text-sm whitespace-pre-wrap leading-relaxed">{selectedDevice.specs.notes}</p>
                                            </div>
                                        )}

                                        {/* Recommended Upgrade */}
                                        {selectedDevice.specs?.upgrade && (
                                            <div className="bg-white/[0.02] rounded-xl border border-blue-500/10 p-4">
                                                <h3 className="text-[10px] font-bold text-blue-400/60 uppercase tracking-widest mb-3">Recommended Upgrade</h3>
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="text-white/80 font-semibold text-sm">{selectedDevice.specs.upgrade.model}</p>
                                                        <p className="text-white/25 text-xs mt-0.5">{selectedDevice.specs.upgrade.description}</p>
                                                    </div>
                                                    <span className="text-emerald-400/80 font-bold font-mono text-sm">{selectedDevice.specs.upgrade.price}</span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <button
                                                onClick={handleEditDevice}
                                                className="w-full py-3 bg-white/[0.04] hover:bg-white/[0.08] text-white/50 hover:text-white border border-white/[0.06] hover:border-white/[0.12] rounded-xl transition-all flex items-center justify-center gap-2 text-[10px] font-semibold uppercase tracking-widest"
                                            >
                                                <Edit3 size={14} />
                                                Edit Device
                                            </button>
                                            <button
                                                onClick={handleDeleteDevice}
                                                className="w-full py-3 bg-red-500/5 hover:bg-red-500/10 text-red-400/50 hover:text-red-400 border border-red-500/10 hover:border-red-500/20 rounded-xl transition-all flex items-center justify-center gap-2 text-[10px] font-semibold uppercase tracking-widest"
                                            >
                                                <Trash2 size={14} />
                                                Remove Device
                                            </button>
                                        </div>
                                    </>
                                )}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="empty-state"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex flex-col items-center justify-center h-full text-center"
                            >
                                <Server size={40} className="mb-4 text-white/5" />
                                <p className="text-white/15 text-sm">Select an empty slot to add a device,</p>
                                <p className="text-white/10 text-xs mt-1">or click a device to view properties.</p>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RackBuilder;
